import type { IconTileAppearance } from '@atlaskit/icon/types';
import {
	type ActionKeys,
	STUDIO_TRIGGER_SOLUTION_PLAN_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import { APPEARANCES } from './constants';
import type { Build } from './types';

export const isBuildEnabled = (build: Build, builds: Build[]): boolean => {
	return (
		build.state !== 'SUCCESS' &&
		(build.type !== 'ECOSYSTEM_APP' || build.state !== 'IN_PROGRESS') &&
		build.dependencies.every(
			(dependency) => builds.find((b) => b.number === dependency)?.state === 'SUCCESS',
		)
	);
};

export const getCurrentBuild = (builds: Build[], currentBuildNumber: number): Build => {
	return builds.find((build) => build.number === currentBuildNumber) ?? builds[builds.length - 1];
};

export const getCurrentBuildIndex = (builds: Build[], currentBuildNumber: number): number => {
	const index = builds.findIndex((build) => build.number === currentBuildNumber);
	return index === -1 ? builds.length - 1 : index;
};

export const isFullyBuilt = (builds: Build[]): boolean => {
	return builds.every((build) => build.state === 'SUCCESS');
};

const BUILD_TYPE_OFFSET: Record<string, number> = {
	AUTOMATION_RULE: 0,
	ECOSYSTEM_APP: 3,
	ROVO_AGENT: 6,
};

export const getAppearanceForIndex = (index: number, buildType: string): IconTileAppearance => {
	const offset = BUILD_TYPE_OFFSET[buildType] ?? 0;
	return APPEARANCES[(index + offset) % APPEARANCES.length];
};

export const filterStudioTriggerSolutionPlanActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.StudioTriggerSolutionPlan]>[] => {
	const filteredActions = actions.filter((action) => {
		if (action.key !== STUDIO_TRIGGER_SOLUTION_PLAN_KEY) {
			throw new Error(`Invalid action key for ${STUDIO_TRIGGER_SOLUTION_PLAN_KEY}`);
		}
		return action;
	});
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.StudioTriggerSolutionPlan]
	>[];
};

const getPathname = (): string => {
	return typeof window !== 'undefined' ? window.location.pathname : '';
};

export const getWorkspaceIdFromUrl = (): string | null => {
	const workspaceIdMatch = getPathname().match(/\/w\/([^\/]+)/);
	return workspaceIdMatch ? workspaceIdMatch[1] : null;
};

export const getSpaceIdFromUrl = (): string | null => {
	const spaceIdMatch = getPathname().match(/\/spaces\/([^\/]+)/);
	return spaceIdMatch ? spaceIdMatch[1] : null;
};
